import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "./api";
import { handleResponseStatus, updateIdField, updateItem } from "./utils";

const initialState = {
    items: [],
    globalProducts: [],
    selectedData: [],
    productHistory: [],
    productReport: [],
    productAlert: [],
    fetchStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchGlobalProductsStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchProductReportStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchByIdStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed',
    fetchHistoryStatus: "idle",
    fetchProductAlertStatus: "idle",
    editStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    productManageStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    updateStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    pagination: {},
    historyPagination: {},
    filter: {
        startDate: '',
        endDate: ''
    },
    error: null
};


export const fetchGlobalProducts = createAsyncThunk('tag/fetchGlobalProducts', async ({ scopeId, roleSign, scope }) => {
    try {
        let response;
        if (scope.toLowerCase() === "restaurant") {
            response = await api.get(`/${roleSign}/list/global/products?id=${scopeId}`);
        } else {
            response = await api.get(`/${roleSign}/list/restaurant/products?id=${scopeId}`);
        }
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
})

export const fetchProducts = createAsyncThunk('product/fetchProducts', async ({
    scope,
    scopeId,
    roleSign,
    page,
    limit,
    orderPage = false,
    orderId
} = {}) => {
    try {
        let response;
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);

        const queryString = params.toString();
        const fullParams = queryString ? `&${queryString}` : '';
        if (scope.toLowerCase() === "restaurant") {
            response = await api.get(`/${roleSign}/restaurentProductTemplate?id=${scopeId}${fullParams}`);
        } else if (orderPage) {
            let url = `/${roleSign}/order/product?id=${scopeId}`;
            //Display the list of products when editing an order
            if (orderId) {
                url += `&orderId=${orderId}`;
            }
            response = await api.get(url);
        } else {
            response = await api.get(`/${roleSign}/product?id=${scopeId}${fullParams}`);
        }
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const deleteProduct = createAsyncThunk('product/deleteProduct', async (id) => {
    try {
        const response = await api.delete(`/adg/product/${id}`);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const editProduct = createAsyncThunk('product/editProduct', async ({ roleSign, scope, data, id }) => {
    try {
        const endpoint =
            scope.toLowerCase() === 'restaurant'
                ? `/${roleSign}/restaurentProductTemplate/${id}`
                : `/${roleSign}/product/${id}`;

        const response = await api.patch(endpoint, data, {
            useMultipart: true,
        });

        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
}
);


export const updateProducts = createAsyncThunk('product/updateProducts', async ({
    scopeId,
    scope,
    roleSign,
    products
}) => {
    const data = {
        id: scopeId,
        products
    };
    try {
        let response;
        if (scope.toLowerCase() === "restaurant") {
            response = await api.post(`/${roleSign}/restaurentProductTemplate`, data);
        } else {
            response = await api.post(`/${roleSign}/product`, data);
        }
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchProductById = createAsyncThunk('product/fetchProductById', async ({ scope, roleSign, id }) => {
    try {
        let response;
        if (scope.toLowerCase() === "restaurant") {
            response = await api.get(`/${roleSign}/restaurentProductTemplate/${id}`);
        } else {
            response = await api.get(`/${roleSign}/product/${id}`);
        }
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchProductAlert = createAsyncThunk('product/fetchProductAlert', async ({ scope, roleSign, scopeId }) => {
    try {
        const response = await api.get(`/${roleSign}/product/lowStockNotify?id=${scopeId}`);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchProductHistory = createAsyncThunk('product/fetchProductHistory', async ({
    id,
    scopeId,
    roleSign,
    startDate,
    endDate,
    page,
    limit,
} = {}) => {
    try {
        let response;
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        const queryString = params.toString();
        const fullParams = queryString ? `&${queryString}` : '';

        response = await api.get(`/${roleSign}/report/showProductDetails/${id}?id=${scopeId}${fullParams}`);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
})

export const fetchProductReport = createAsyncThunk('product/fetchProductReport', async ({
    roleSign,
    scopeId,
    dateParams
}) => {
    try {
        let response = await api.get(`/${roleSign}/report/products?id=${scopeId}${dateParams}`);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
})

export const manageProduct = createAsyncThunk('product/manageProduct', async ({ roleSign, data }) => {
    try {
        const response = await api.post(`${roleSign}/product/updatestockprice`, data);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});
export const addRecommendedProducts = createAsyncThunk('product/recommendedProducts', async ({ roleSign, data }) => {
    try {
        const response = await api.post(`${roleSign}/product/setRecommendedProducts`, data);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});


const productSlice = createSlice({
    name: "product",
    initialState,
    reducers: {
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetEditStatus(state) {
            state.editStatus = 'idle';
        },
        resetProductReportStatus(state) {
            state.fetchProductReportStatus = 'idle';
        },
        resetProductManageStatus(state) {
            state.productManageStatus = 'idle';
        },
        resetAddRecommendedProductsStatus(state) {
            state.addRecommendedProductsStatus = 'idle';
        },
        onChangeFilter(state, action) {
            const { key, value } = action.payload;
            if (state.filter.hasOwnProperty(key)) {
                state.filter[key] = value;
            }
        }
    },
    extraReducers: (builder) => {
        builder

            //get Global Products
            .addCase(fetchGlobalProducts.pending, (state) => {
                state.fetchGlobalProductsStatus = 'loading';
            })
            .addCase(fetchGlobalProducts.fulfilled, (state, action) => {
                const {
                    fetchGlobalProductsStatus,
                    error
                } = handleResponseStatus(action.payload, 'fetchGlobalProductsStatus');
                state.globalProducts = fetchGlobalProductsStatus === "success" ? updateIdField(action.payload.data.data) : state.globalProducts;
                state.selectedData = fetchGlobalProductsStatus === "success" ? action.payload.data.selectedData : state.selectedData;
                state.fetchGlobalProductsStatus = fetchGlobalProductsStatus;
                state.error = error;
            })
            .addCase(fetchGlobalProducts.rejected, (state, action) => {
                state.fetchGlobalProductsStatus = 'failed';
                state.error = action.error.message;
            })


            // Fetch Products
            .addCase(fetchProducts.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                const { fetchStatus, error } = handleResponseStatus(action.payload, 'fetchStatus');
                const { data, ...pagination } = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Add Recommended Products
            .addCase(addRecommendedProducts.pending, (state) => {
                state.addRecommendedProductsStatus = 'loading';
            })
            .addCase(addRecommendedProducts.fulfilled, (state, action) => {
                const { addRecommendedProductsStatus, error } = handleResponseStatus(action.payload, 'addRecommendedProductsStatus');
                state.addRecommendedProductsStatus = addRecommendedProductsStatus;
                state.error = error;
            })
            .addCase(addRecommendedProducts.rejected, (state, action) => {
                state.addRecommendedProductsStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Product alert
            .addCase(fetchProductAlert.pending, (state) => {
                state.fetchProductAlertStatus = 'loading';
            })
            .addCase(fetchProductAlert.fulfilled, (state, action) => {
                const {
                    fetchProductAlertStatus,
                    error
                } = handleResponseStatus(action.payload, 'fetchProductAlertStatus');
                const { products: data = [], ...pagination } = action.payload?.data || {};
                state.productAlert = fetchProductAlertStatus === "success" ? updateIdField(data) : state.productAlert;
                state.fetchProductAlertStatus = fetchProductAlertStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchProductAlert.rejected, (state, action) => {
                state.fetchProductAlertStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Product by ID
            .addCase(fetchProductById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchProductById.fulfilled, (state, action) => {
                const { fetchByIdStatus, error } = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchProductById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Edit Product
            .addCase(editProduct.pending, (state) => {
                state.editStatus = 'loading';
            })
            .addCase(editProduct.fulfilled, (state, action) => {
                const { editStatus, error } = handleResponseStatus(action.payload, 'editStatus');
                state.editStatus = editStatus;
                state.error = error;
            })
            .addCase(editProduct.rejected, (state, action) => {
                state.editStatus = 'failed';
                state.error = action.error.message;
            })

            // Update Product
            .addCase(updateProducts.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateProducts.fulfilled, (state, action) => {
                const { updateStatus, error } = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateProducts.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete Product
            .addCase(deleteProduct.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteProduct.fulfilled, (state, action) => {
                const { deleteStatus, error } = handleResponseStatus(action.payload, 'deleteStatus');
                state.deleteStatus = deleteStatus;
                state.items = state.items.filter((item) => item.id !== action.payload.data.data._id);
                state.error = error;
            })
            .addCase(deleteProduct.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Product History
            .addCase(fetchProductHistory.pending, (state) => {
                state.fetchHistoryStatus = 'loading';
            })
            .addCase(fetchProductHistory.fulfilled, (state, action) => {
                const { fetchHistoryStatus, error } = handleResponseStatus(action.payload, 'fetchHistoryStatus');
                const { data, ...pagination } = action.payload.data;
                state.productHistory = fetchHistoryStatus === "success" ? updateIdField(data) : state.productHistory;
                state.fetchHistoryStatus = fetchHistoryStatus;
                state.historyPagination = pagination;
                state.error = error;
            })
            .addCase(fetchProductHistory.rejected, (state, action) => {
                state.fetchHistoryStatus = 'failed';
                state.error = action.error.message;
            })
            // Fetch Product report
            .addCase(fetchProductReport.pending, (state) => {
                state.fetchProductReportStatus = 'loading';
            })
            .addCase(fetchProductReport.fulfilled, (state, action) => {
                const {
                    fetchProductReportStatus,
                    error
                } = handleResponseStatus(action.payload, 'fetchProductReportStatus');
                const { data } = action.payload.data;
                state.productReport = fetchProductReportStatus === "success" ? updateIdField(data) : state.productReport;
                state.fetchProductReportStatus = fetchProductReportStatus;
                state.error = error;
            })
            .addCase(fetchProductReport.rejected, (state, action) => {
                state.fetchProductReportStatus = 'failed';
                state.error = action.error.message;
            })

            //manage product
            .addCase(manageProduct.pending, (state) => {
                state.productManageStatus = 'loading';
            })
            .addCase(manageProduct.fulfilled, (state, action) => {
                const { productManageStatus, error } = handleResponseStatus(action.payload, 'productManageStatus');
                state.productManageStatus = productManageStatus;
                state.error = error;
            })
            .addCase(manageProduct.rejected, (state, action) => {
                state.productManageStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetCreateStatus,
    onChangeFilter,
    resetUpdateStatus,
    resetDeleteStatus,
    resetEditStatus,
    resetProductReportStatus,
    resetProductManageStatus,
    resetAddRecommendedProductsStatus
} = productSlice.actions;
export default productSlice.reducer;
